import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Heart, MessageCircle, ArrowLeft, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MatchesPage = ({ user }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAcceptedMatches();
  }, []);

  const fetchAcceptedMatches = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/matches/accepted`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="animate-pulse text-2xl font-light text-rose-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Button
          data-testid="back-to-dashboard-btn"
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-rose-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <h1 className="text-5xl font-bold text-rose-900 mb-8" style={{fontFamily: 'Cormorant Garamond, serif'}}>
          Your Matches
        </h1>

        {matches.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="p-12 text-center">
              <Heart className="w-16 h-16 text-rose-300 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-rose-900 mb-2">No matches yet</h3>
              <p className="text-gray-600">Start reviewing your daily matches to find connections!</p>
              <Button
                data-testid="view-daily-matches-btn"
                onClick={() => navigate('/dashboard')}
                className="mt-6 bg-rose-500 hover:bg-rose-600"
              >
                View Daily Matches
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matches.map(({ match, user: matchUser }) => (
              <Card
                key={match.id}
                data-testid={`match-card-${match.id}`}
                className="overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border-rose-100"
              >
                <div className="h-64 bg-gradient-to-br from-rose-200 to-amber-200 flex items-center justify-center">
                  {matchUser.picture ? (
                    <img src={matchUser.picture} alt={matchUser.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-24 h-24 text-rose-400" />
                  )}
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                    {matchUser.name}
                  </CardTitle>
                  <CardDescription>
                    {matchUser.location?.city && `${matchUser.location.city}, `}
                    {matchUser.location?.country}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Compatibility</span>
                      <span className="text-lg font-bold text-rose-600">{Math.round(match.compatibility_score)}%</span>
                    </div>
                    <Progress value={match.compatibility_score} className="h-2" />
                  </div>
                  <Button
                    data-testid={`message-btn-${match.id}`}
                    onClick={() => navigate(`/messages/${match.id}`)}
                    className="w-full bg-rose-500 hover:bg-rose-600"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Message
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchesPage;